import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../APIs/axios";
import {
  approveProviderVerification,
  getProviderVerificationDetail,
  getProviderVerificationSummary,
  listProviderVerifications,
  rejectProviderVerification,
  requestProviderVerificationDocuments,
} from "../APIs/adminProviderVerificationApi";
import { PROVIDER_VERIFICATION_DOCUMENT_TYPES } from "../APIs/providerApi";

const TABS = [
  { key: "PENDING_REVIEW", label: "Pending" },
  { key: "VERIFIED", label: "Approved" },
  { key: "REJECTED", label: "Rejected" },
  { key: "DOCUMENTS_REQUESTED", label: "Documents Requested" },
];

const REJECTION_REASONS = [
  "Unable to verify identity",
  "Invalid document",
  "Expired license",
  "Duplicate provider",
  "Other",
];

const DOC_LABELS = {
  GOVERNMENT_ID: "Government ID",
  MEDICAL_LICENSE: "Medical License",
  DEA_LICENSE: "DEA License",
  BOARD_CERTIFICATION: "Board Certification",
  NURSING_LICENSE: "Nursing License",
  PHYSICIAN_ASSISTANT_LICENSE: "Physician Assistant License",
  THERAPIST_LICENSE: "Therapist License",
  EMPLOYMENT_VERIFICATION: "Employment Verification",
  FACILITY_BADGE: "Facility Badge",
  BUSINESS_CARD: "Business Card",
  MALPRACTICE_INSURANCE: "Malpractice Insurance",
  OTHER: "Other",
};

function formatDate(value) {
  if (!value) return "N/A";
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function statusLabel(status) {
  if (status === "PENDING_REVIEW") return "PENDING";
  if (status === "DOCUMENTS_REQUESTED") return "DOCUMENTS REQUESTED";
  return status || "N/A";
}

function DocumentPreviewModal({ document, onClose }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!document) return undefined;
    let objectUrl;
    setLoading(true);
    setError(null);

    api
      .get(`/api/v1/documents/${document.documentId}/view`, {
        responseType: "blob",
      })
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch((err) => {
        console.error("Failed to load document preview:", err);
        setError("Unable to load document preview.");
      })
      .finally(() => setLoading(false));

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [document]);

  if (!document) return null;

  const isImage = document.mimeType?.startsWith("image/");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-lg bg-white shadow-lg">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <h3 className="text-lg font-semibold">{document.fileName}</h3>
          <button
            className="text-gray-500 hover:text-gray-800"
            onClick={onClose}
          >
            x
          </button>
        </div>
        <div className="flex items-center gap-3 border-b px-5 py-3 text-sm">
          {url && (
            <>
              <a
                className="font-medium text-blue-600"
                href={url}
                download={document.fileName}
              >
                Download
              </a>
              <a
                className="font-medium text-blue-600"
                href={url}
                target="_blank"
                rel="noreferrer"
              >
                Open Original
              </a>
            </>
          )}
        </div>
        <div className="min-h-[520px] flex-1 overflow-auto bg-gray-50 p-4">
          {loading && (
            <p className="text-sm text-gray-500">Loading document...</p>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {url && isImage && (
            <img
              src={url}
              alt={document.fileName}
              className="mx-auto max-h-[70vh] max-w-full"
            />
          )}
          {url && !isImage && (
            <iframe
              title={document.fileName}
              src={url}
              className="h-[70vh] w-full rounded border bg-white"
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProviderVerificationQueue() {
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("PENDING_REVIEW");
  const [search, setSearch] = useState("");
  const [requests, setRequests] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState(REJECTION_REASONS[0]);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestedDocs, setRequestedDocs] = useState(["MEDICAL_LICENSE"]);
  const [requestMessage, setRequestMessage] = useState("");

  const selectedTabLabel = useMemo(
    () => TABS.find((tab) => tab.key === status)?.label || "Pending",
    [status],
  );

  const refreshSummary = useCallback(async () => {
    setSummary(await getProviderVerificationSummary());
  }, []);

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listProviderVerifications({
        status,
        q: search,
        size: 25,
      });
      setRequests(data?.content || []);
      const first = data?.content?.[0]?.providerId || null;
      setSelectedId((current) => current || first);
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    refreshSummary();
  }, [refreshSummary]);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  useEffect(() => {
    const timeout = setTimeout(() => refreshList(), 300);
    return () => clearTimeout(timeout);
  }, [refreshList]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    getProviderVerificationDetail(selectedId)
      .then((data) => {
        setDetail(data);
        setNotes(data?.notes || "");
      })
      .catch((err) => {
        console.error("Failed to load verification detail:", err);
        setDetail(null);
      });
  }, [selectedId]);

  const refreshCurrent = async () => {
    await refreshSummary();
    await refreshList();
    if (selectedId) {
      const data = await getProviderVerificationDetail(selectedId);
      setDetail(data);
      setNotes(data?.notes || "");
    }
  };

  const handleApprove = async () => {
    if (!detail) return;
    setActionError(null);
    try {
      const updated = await approveProviderVerification(
        detail.providerId,
        notes,
      );
      setDetail(updated);
      await refreshCurrent();
    } catch (err) {
      console.error("Approve failed:", err);
      setActionError(
        err?.response?.data?.message || "Unable to approve provider.",
      );
    }
  };

  const handleReject = async () => {
    if (!detail) return;
    setActionError(null);
    try {
      const updated = await rejectProviderVerification(
        detail.providerId,
        rejectReason,
        notes,
      );
      setDetail(updated);
      setRejectOpen(false);
      await refreshCurrent();
    } catch (err) {
      console.error("Reject failed:", err);
      setActionError(
        err?.response?.data?.message || "Unable to reject request.",
      );
    }
  };

  const handleRequestDocs = async () => {
    if (!detail) return;
    setActionError(null);
    try {
      const updated = await requestProviderVerificationDocuments(
        detail.providerId,
        requestedDocs,
        requestMessage,
        notes,
      );
      setDetail(updated);
      setRequestOpen(false);
      await refreshCurrent();
    } catch (err) {
      console.error("Request documents failed:", err);
      setActionError(
        err?.response?.data?.message || "Unable to request documents.",
      );
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Verification Dashboard
        </h2>
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          <SummaryCard
            label="Pending Reviews"
            value={summary?.pendingReviews ?? 0}
          />
          <SummaryCard
            label="Approved Today"
            value={summary?.approvedToday ?? 0}
          />
          <SummaryCard
            label="Rejected Today"
            value={summary?.rejectedToday ?? 0}
          />
          <SummaryCard
            label="Documents Requested"
            value={summary?.documentsRequested ?? 0}
          />
        </div>
      </div>

      <section className="rounded-lg border bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 border-b pb-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold">
              Provider Verification Requests
            </h2>
            <p className="text-sm text-gray-500">{selectedTabLabel}</p>
          </div>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm md:w-80"
            placeholder="Search provider, NPI, email, specialty"
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setStatus(tab.key);
                setSelectedId(null);
              }}
              className={`rounded px-3 py-2 text-sm font-medium ${
                status === tab.key
                  ? "bg-teal-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(420px,0.9fr)]">
          <div className="overflow-hidden rounded border">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">Provider</th>
                  <th className="px-4 py-3">Specialty</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td className="px-4 py-5 text-gray-500" colSpan={4}>
                      Loading requests...
                    </td>
                  </tr>
                )}
                {!loading &&
                  requests.map((request) => (
                    <tr
                      key={request.providerId}
                      onClick={() => setSelectedId(request.providerId)}
                      className={`cursor-pointer border-t hover:bg-teal-50 ${
                        selectedId === request.providerId ? "bg-teal-50" : ""
                      }`}
                    >
                      <td className="px-4 py-3 font-medium">
                        {request.providerName}
                      </td>
                      <td className="px-4 py-3">{request.specialty}</td>
                      <td className="px-4 py-3">
                        {formatDate(request.submittedAt)}
                      </td>
                      <td className="px-4 py-3">
                        {statusLabel(request.status)}
                      </td>
                    </tr>
                  ))}
                {!loading && requests.length === 0 && (
                  <tr>
                    <td className="px-4 py-5 text-gray-500" colSpan={4}>
                      No verification requests found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="rounded border bg-gray-50 p-5">
            {!detail ? (
              <p className="text-sm text-gray-500">
                Select a request to review.
              </p>
            ) : (
              <div className="space-y-6">
                <section>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold">
                        Provider Verification
                      </h3>
                      <p className="text-sm text-gray-500">
                        {detail.providerName}
                      </p>
                    </div>
                    <span className="rounded border bg-white px-2 py-1 text-xs font-semibold">
                      {statusLabel(detail.status)}
                    </span>
                  </div>
                  <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <Info label="NPI" value={detail.npi} />
                    <Info label="Specialty" value={detail.specialty} />
                    <Info label="Email" value={detail.email} />
                    <Info
                      label="Claimed At"
                      value={formatDate(detail.claimedAt)}
                    />
                  </dl>
                </section>

                <section className="grid gap-3 md:grid-cols-2">
                  <div className="rounded border bg-white p-4">
                    <h4 className="font-semibold">NPI Registry</h4>
                    <p className="mt-2 text-sm">
                      {detail.npiRegistry?.name || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      {detail.npiRegistry?.specialty || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      {detail.npiRegistry?.location || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      NPI: {detail.npiRegistry?.npi || "N/A"}
                    </p>
                  </div>
                  <div className="rounded border bg-white p-4">
                    <h4 className="font-semibold">Uploaded Documents</h4>
                    <div className="mt-2 space-y-2">
                      {detail.documents?.length ? (
                        detail.documents.map((doc) => (
                          <div
                            key={doc.documentId}
                            className="rounded border p-3 text-sm"
                          >
                            <div className="font-medium">{doc.fileName}</div>
                            <div className="text-gray-500">
                              Uploaded: {formatDate(doc.uploadTime)}
                            </div>
                            <div className="text-gray-500">
                              Status: {doc.status}
                            </div>
                            <button
                              className="mt-2 font-medium text-blue-600"
                              onClick={() => setPreviewDoc(doc)}
                            >
                              View
                            </button>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">
                          No documents uploaded.
                        </p>
                      )}
                    </div>
                  </div>
                </section>

                <section className="rounded border bg-white p-4">
                  <h4 className="font-semibold">Verification Decision</h4>
                  <label className="mt-3 block text-sm">
                    Notes
                    <textarea
                      value={notes}
                      onChange={(event) => setNotes(event.target.value)}
                      className="mt-1 h-24 w-full rounded border border-gray-300 px-3 py-2"
                    />
                  </label>
                  {actionError && (
                    <p className="mt-3 text-sm text-red-600">{actionError}</p>
                  )}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={handleApprove}
                      className="rounded bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
                    >
                      Approve Provider
                    </button>
                    <button
                      onClick={() => setRejectOpen(true)}
                      className="rounded bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
                    >
                      Reject Request
                    </button>
                    <button
                      onClick={() => setRequestOpen(true)}
                      className="rounded bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600"
                    >
                      Request Additional Documents
                    </button>
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </section>

      {previewDoc && (
        <DocumentPreviewModal
          document={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}

      {rejectOpen && (
        <DecisionModal
          title="Reject Request"
          onClose={() => setRejectOpen(false)}
          onSubmit={handleReject}
          submitLabel="Reject"
        >
          <div className="space-y-2">
            {REJECTION_REASONS.map((reason) => (
              <label key={reason} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  checked={rejectReason === reason}
                  onChange={() => setRejectReason(reason)}
                />
                {reason}
              </label>
            ))}
          </div>
        </DecisionModal>
      )}

      {requestOpen && (
        <DecisionModal
          title="Request Additional Documents"
          onClose={() => setRequestOpen(false)}
          onSubmit={handleRequestDocs}
          submitLabel="Send Request"
        >
          <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-2">
              {PROVIDER_VERIFICATION_DOCUMENT_TYPES.map((type) => (
                <label key={type} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={requestedDocs.includes(type)}
                    onChange={(event) => {
                      setRequestedDocs((prev) =>
                        event.target.checked
                          ? [...prev, type]
                          : prev.filter((value) => value !== type),
                      );
                    }}
                  />
                  {DOC_LABELS[type] || type}
                </label>
              ))}
            </div>
            <label className="block text-sm">
              Message
              <textarea
                value={requestMessage}
                onChange={(event) => setRequestMessage(event.target.value)}
                className="mt-1 h-24 w-full rounded border border-gray-300 px-3 py-2"
                placeholder="Your facility badge alone is insufficient for verification."
              />
            </label>
          </div>
        </DecisionModal>
      )}
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-gray-900">{value}</div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <dt className="font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-gray-900">{value || "N/A"}</dd>
    </div>
  );
}

function DecisionModal({ title, children, onClose, onSubmit, submitLabel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            className="text-gray-500 hover:text-gray-800"
            onClick={onClose}
          >
            x
          </button>
        </div>
        <div className="mt-4">{children}</div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            className="rounded border px-4 py-2 text-sm"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="rounded bg-teal-600 px-4 py-2 text-sm font-semibold text-white"
            onClick={onSubmit}
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
